import {
    Autocomplete, Box, Button, FormControl, FormHelperText, IconButton,
    InputAdornment, InputLabel, OutlinedInput, Paper, TextField, Grid,
    Stack, Checkbox, Typography, Card, CardContent, Divider, Chip
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Visibility, VisibilityOff, Person, Security, Email, ManageAccounts } from '@mui/icons-material';
import { addManageRefrsh, updateManageByIdRefresh } from '../../actions/mangeAction';
import { resetSelectedUser, resetState } from '../../slices/manageSlice';
import { showErrorToast } from '../../slices/toastSlice';
import Loader from '../Loader';

const AddUser = () => {
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate()
    const id = parseInt(searchParams.get('id'));
    const selectedUser = useSelector((state) => state.manage.selectedUser) || {};


    const { updated, added, hasError, errorMessage, loading } = useSelector((state) => state.manage) || {};
    const { roleList = [] } = useSelector((state) => state.form.data) || {};

    const rolesOptions = useMemo(() => roleList, [roleList]);
    const sortedRolesOptions = useMemo(() => [...rolesOptions].sort((a, b) => (a?.name || '').localeCompare(b?.name || '')), [rolesOptions]);

    const validationSchema = yup.object().shape({
        userName: yup.string().required('User name is required'),
        firstName: yup.string().required('First name is required'),
        lastName: yup.string().required('Last name is required'),
        email: yup.string().nullable().email('Invalid email'),
        roles: yup.array().min(1, 'Please select at least one role'),
        password: yup
            .string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters long')
            .matches(
                // eslint-disable-next-line no-useless-escape
                /^(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]).*$/,
                'Password must contain at least one special character'
            ),


    });
    const validationSchemaWithoutPassword = yup.object().shape({
        userName: yup.string().required('User name is required'),
        firstName: yup.string().required('First name is required'),
        lastName: yup.string().required('Last name is required'),
        email: yup.string().nullable().email('Invalid email'),
        roles: yup.array().min(1, 'Please select at least one role'),


    });
    const formik = useFormik({
        initialValues: {
            userName: selectedUser.userName || '',
            firstName: selectedUser.firstName || '',
            lastName: selectedUser.lastName || '',
            email: selectedUser.email || null,
            password: '',
            roles: selectedUser.roles || [],


        },
        validationSchema: !id ? validationSchema : validationSchemaWithoutPassword,
        onSubmit: (values) => handleSaveUpdate(values),
        enableReinitialize: true,
        validateOnMount: true,
    });
    const handleSaveUpdate = (data) => {
        const { country, ...rest } = data;
        const payload = {
            ...rest,
            roles: data.roles.map(item => item.name),
        }
        if (id) {
            dispatch(updateManageByIdRefresh({ mode: 'user', id, data: payload }));
            return;
        }
        dispatch(addManageRefrsh({ mode: 'user', data: payload }));

    }
    useEffect(() => {
        if ((updated || added) && !hasError) {
            navigate('/user/userList');
        }
        return () => {
            dispatch(resetState());
            dispatch(resetSelectedUser());
        };
    }, [updated, added, hasError, navigate, dispatch]);

    // removed unused handleKeyDown after form wrapper submit handling

    useEffect(() => {
        if (hasError && errorMessage) {
            dispatch(showErrorToast(errorMessage));
        }
    }, [hasError, errorMessage, dispatch]);
    if (loading === 'pending') {
        return (
            <Loader />
        )
    }

    return (
        <Box sx={{ width: '100%', bgcolor: '#F7F8FA', minHeight: '100vh', p: { xs: 0, md: 2 } }}>

            <Box sx={{ display: 'flex', justifyContent: "space-between", gap: 2, mb: 3, mt: { xs: 2, md: 0 } }}>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: { xs: 2, md: 0 } }}>
                    <ManageAccounts sx={{ fontSize: 40, color: '#0B3C5D' }} />
                    <Typography variant="h4" sx={{ color: '#0B3C5D', fontWeight: 800, letterSpacing: 1 }}>
                        {t('User Management')}
                    </Typography>

                </Box>



            </Box>

            <Paper
                sx={{
                    p: { xs: 1, md: 3 },
                    minHeight: 200,
                    flexGrow: 1,
                    mt: 3,
                    background: '#fff',
                    borderRadius: 4,
                    boxShadow: '0 8px 32px 0 rgba(11, 60, 93, 0.10)',
                    border: '1px solid #e9ecef',
                }}
            >

                <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ px: 2, pb: 2 }}>
                    {/* Account Section */}
                    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Person sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Account Information')}</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={t('User name')}
                                        name="userName"
                                        variant="outlined"
                                        value={formik.values.userName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.userName && Boolean(formik.errors.userName)}
                                        helperText={formik.touched.userName && formik.errors.userName}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                {!id && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel htmlFor="password">{t('Password')}</InputLabel>
                                            <OutlinedInput
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={formik.values.password}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.password && Boolean(formik.errors.password)}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                label={t('Password')}
                                                sx={{ borderRadius: 2 }}
                                            />
                                            <FormHelperText variant='filled' sx={{ color: 'error.main' }}>
                                                {formik.touched.password && formik.errors.password}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Personal Information Section */}
                    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Email sx={{ mr: 1, color: 'secondary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Personal Information')}</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={t('First name')}
                                        variant="outlined"
                                        name="firstName"
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                        helperText={formik.touched.firstName && formik.errors.firstName}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={t('Last name')}
                                        name="lastName"
                                        variant="outlined"
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                        helperText={formik.touched.lastName && formik.errors.lastName}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('Email')}
                                        variant="outlined"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        helperText={formik.touched.email && formik.errors.email}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Roles Section */}
                    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Security sx={{ mr: 1, color: 'warning.main' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Access Control')}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                        label={`${formik.values.roles?.length || 0} ${t('roles selected')}`}
                                        size="small"
                                        color={formik.values.roles?.length > 0 ? 'success' : 'default'}
                                        variant="outlined"
                                    />
                                </Box>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">{t('Select user roles and permissions')}</Typography>
                                <Box>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => formik.setFieldValue('roles', sortedRolesOptions)}
                                        sx={{ mr: 1 }}
                                    >
                                        {t('Select All')}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        onClick={() => formik.setFieldValue('roles', [])}
                                    >
                                        {t('Clear')}
                                    </Button>
                                </Box>
                            </Stack>

                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                limitTags={3}
                                options={sortedRolesOptions}
                                getOptionLabel={(option) => option?.name}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                filterSelectedOptions
                                onChange={(event, value) => {
                                    formik.setFieldValue('roles', value);
                                }}
                                value={formik.values.roles}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                        <Box>
                                            <Typography variant="body2">{option?.name}</Typography>
                                            {option?.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder={t('Select roles and permissions')}
                                        name='roles'
                                        error={formik.touched.roles && Boolean(formik.errors.roles)}
                                        helperText={formik.touched.roles && formik.errors.roles}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                )}
                                onBlur={formik.handleBlur}
                            />
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <Paper
                        elevation={2}
                        sx={{
                            bottom: 16,
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(90deg, #f8f9fa 0%, #ffffff 100%)',
                            border: '1px solid #e9ecef'
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="flex-end"
                            alignItems="center"
                            spacing={2}
                        >
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => {
                                    navigate('/user/userList');
                                    dispatch(resetSelectedUser());
                                }}
                                sx={{
                                    minWidth: { sm: 120 },
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                size="large"
                                type="submit"
                                disabled={!formik.isValid}
                                sx={{
                                    minWidth: { sm: 120 },
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                                }}
                            >
                                {t('Save User')}
                            </Button>
                        </Stack>
                    </Paper>
                </Box>
            </Paper>
        </Box>
    )
}

export default AddUser